/**
 * Which churches lie along a route, where each one joins it, and roughly what
 * the detour costs.
 *
 * masstimes.org answers "the churches nearest this point", nearest first, a
 * page at a time. A page whose farthest result is `r` km away therefore
 * certifies every church within `r` of the query point. Covering a corridor of
 * half-width `w` around the route is then a disk-cover problem (analysis §6): a
 * certified disk of radius `r > w` centred on the route covers the corridor for
 * `2·√(r² − w²)` km of route, so the next query goes that far ahead. Sparse
 * countryside gives big disks and long steps; a city gives small ones, where we
 * fetch another page or narrow the corridor locally.
 */

import { haversineKm, type LatLng } from "@viamissa/geo";
import type { Church, ChurchPage } from "@viamissa/masstimes";
import type { Route } from "@viamissa/routing";

/** Where a point meets the route. */
export type Projection = {
  /** Segment start index in `route.shape`. */
  index: number;
  /** Fraction along that segment, 0–1. */
  t: number;
  /** The closest point on the route. */
  point: LatLng;
  /** Straight-line distance from the route, km. */
  offKm: number;
  /** Distance along the route to the closest point, km. */
  alongKm: number;
  /** Drive time from the start to the closest point, minutes (`R(s)` in the analysis). */
  alongMin: number;
};

export type CorridorChurch = {
  church: Church;
  join: Projection;
  /**
   * Estimated drive from the route to the church and back, minutes. An
   * over-estimate on purpose (analysis §5); the planner re-routes its picks.
   */
  toMin: number;
  backMin: number;
  /** True once `toMin`/`backMin` come from a router rather than the estimate. */
  exact: boolean;
};

const KM_PER_DEG_LAT = 111.32;

/** Closest point on segment a→b to p, using a local flat projection (fine at road scale). */
function projectOntoSegment(p: LatLng, a: LatLng, b: LatLng): { t: number; point: LatLng } {
  const kx = Math.cos((p.lat * Math.PI) / 180) * KM_PER_DEG_LAT;
  const ax = (a.lng - p.lng) * kx;
  const ay = (a.lat - p.lat) * KM_PER_DEG_LAT;
  const bx = (b.lng - p.lng) * kx;
  const by = (b.lat - p.lat) * KM_PER_DEG_LAT;
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSq = dx * dx + dy * dy;
  const t = lengthSq === 0 ? 0 : Math.max(0, Math.min(1, -(ax * dx + ay * dy) / lengthSq));
  return { t, point: { lat: a.lat + (b.lat - a.lat) * t, lng: a.lng + (b.lng - a.lng) * t } };
}

/** Project a point onto the route. */
export function projectOntoRoute(route: Route, p: LatLng): Projection {
  const { shape, cumKm, cumMin } = route;
  let best: Projection | null = null;
  for (let index = 0; index < shape.length - 1; index++) {
    const a = shape[index]!;
    const b = shape[index + 1]!;
    // Cheap reject: a segment whose nearer end is farther than the best by more than its length.
    if (best) {
      const segment = (cumKm[index + 1] ?? 0) - (cumKm[index] ?? 0);
      if (Math.min(haversineKm(p, a), haversineKm(p, b)) - segment > best.offKm) continue;
    }
    const { t, point } = projectOntoSegment(p, a, b);
    const offKm = haversineKm(p, point);
    if (!best || offKm < best.offKm) {
      const km0 = cumKm[index] ?? 0;
      const min0 = cumMin[index] ?? 0;
      best = {
        index,
        t,
        point,
        offKm,
        alongKm: km0 + ((cumKm[index + 1] ?? km0) - km0) * t,
        alongMin: min0 + ((cumMin[index + 1] ?? min0) - min0) * t,
      };
    }
  }
  if (best) return best;
  const only = shape[0] ?? p;
  return { index: 0, t: 0, point: only, offKm: haversineKm(p, only), alongKm: 0, alongMin: 0 };
}

/** The route point `km` from the start. */
export function pointAtKm(route: Route, km: number): LatLng {
  const { shape, cumKm } = route;
  if (km <= 0) return shape[0]!;
  let lo = 0;
  let hi = cumKm.length - 1;
  if (km >= (cumKm[hi] ?? 0)) return shape[hi]!;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if ((cumKm[mid] ?? 0) <= km) lo = mid;
    else hi = mid;
  }
  const span = (cumKm[hi] ?? 0) - (cumKm[lo] ?? 0);
  const t = span > 0 ? (km - (cumKm[lo] ?? 0)) / span : 0;
  const a = shape[lo]!;
  const b = shape[hi]!;
  return { lat: a.lat + (b.lat - a.lat) * t, lng: a.lng + (b.lng - a.lng) * t };
}

/**
 * Off-route drive estimate, minutes, one way: a road-winding factor over the
 * straight line at town speeds, plus parking. Deliberately pessimistic.
 */
export function estimateAccessMinutes(offKm: number): number {
  const ROAD_FACTOR = 1.35;
  const KMH = 45;
  const PARKING_MIN = 3;
  return PARKING_MIN + ((offKm * ROAD_FACTOR) / KMH) * 60;
}

export type FetchPage = (point: LatLng, page: number) => Promise<ChurchPage>;

export type CorridorOptions = {
  route: Route;
  /** Half-width of the corridor, km. */
  halfWidthKm: number;
  fetchPage: FetchPage;
  /** Extra pages to try at one point before narrowing the corridor there. */
  maxPagesPerPoint?: number;
  /** Called after each query with the share of the route covered so far (0–1). */
  onProgress?: (done: number, found: number) => void;
  signal?: AbortSignal;
};

export type CorridorResult = {
  churches: CorridorChurch[];
  /** Queries made, for the record and for tests. */
  queries: number;
  /** Stretches of route (km from start) where the corridor had to narrow, and to what. */
  narrowed: { fromKm: number; toKm: number; halfWidthKm: number }[];
};

/**
 * How far behind the start (or past the end) a church may sit and still count.
 * The search circles reach beyond the route's ends, but a church there means
 * doubling back, and "in the order you reach them" stops being true.
 */
export const END_CAP_KM = 1.5;

/** How far `p` lies before `end`, measured along the road's direction as it leaves `end`. */
function behindKm(p: LatLng, end: LatLng, inward: LatLng): number {
  const kx = Math.cos((end.lat * Math.PI) / 180) * KM_PER_DEG_LAT;
  const dx = (inward.lng - end.lng) * kx;
  const dy = (inward.lat - end.lat) * KM_PER_DEG_LAT;
  const length = Math.hypot(dx, dy);
  if (length === 0) return 0;
  const px = (p.lng - end.lng) * kx;
  const py = (p.lat - end.lat) * KM_PER_DEG_LAT;
  return -(px * dx + py * dy) / length;
}

/** Whether a church sits more than `END_CAP_KM` behind the start or past the end. */
function beyondEnds(route: Route, p: LatLng, join: Projection): boolean {
  const lookKm = Math.min(0.5, route.totalKm / 2);
  if (join.alongKm <= lookKm && behindKm(p, route.shape[0]!, pointAtKm(route, lookKm)) > END_CAP_KM) return true;
  const last = route.shape[route.shape.length - 1]!;
  return join.alongKm >= route.totalKm - lookKm && behindKm(p, last, pointAtKm(route, route.totalKm - lookKm)) > END_CAP_KM;
}

/** Find every church within `halfWidthKm` of the route, in route order. */
export async function searchCorridor(options: CorridorOptions): Promise<CorridorResult> {
  const { route, halfWidthKm, fetchPage, maxPagesPerPoint = 3, onProgress, signal } = options;
  const found = new Map<string, Church>();
  const narrowed: CorridorResult["narrowed"] = [];
  let fullPage = 0;
  let queries = 0;
  const totalKm = route.totalKm;

  for (let km = 0; ; ) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    const point = pointAtKm(route, km);
    let radius = 0;
    let exhausted = false;
    for (let page = 1; page <= maxPagesPerPoint; page++) {
      const result = await fetchPage(point, page);
      queries++;
      fullPage = Math.max(fullPage, result.size);
      for (const church of result.churches) found.set(church.id, church);
      radius = Math.max(radius, result.radiusKm);
      // A short page means there is nothing farther out: everything is certified.
      if (result.size === 0 || result.size < fullPage) {
        exhausted = true;
        break;
      }
      if (radius >= halfWidthKm * 1.25) break;
    }

    // In a dense city the certified disk can be narrower than the corridor; narrow it here.
    const width = exhausted ? halfWidthKm : Math.min(halfWidthKm, radius * 0.7);
    const reach = exhausted ? Infinity : radius;
    const step = Number.isFinite(reach) ? Math.max(0.5, 2 * Math.sqrt(reach * reach - width * width) * 0.8) : totalKm;
    if (width < halfWidthKm) {
      const last = narrowed[narrowed.length - 1];
      const halfWidth = Math.round(width * 10) / 10;
      if (last && last.toKm >= km - 0.01 && last.halfWidthKm === halfWidth) last.toKm = km + step;
      else narrowed.push({ fromKm: km, toKm: Math.min(totalKm, km + step), halfWidthKm: halfWidth });
    }

    onProgress?.(Math.min(1, (km + step) / Math.max(totalKm, 1e-9)), found.size);
    if (km >= totalKm) break;
    km = Math.min(totalKm, km + step);
  }

  const churches: CorridorChurch[] = [];
  for (const church of found.values()) {
    const join = projectOntoRoute(route, church);
    if (join.offKm > halfWidthKm) continue;
    if (beyondEnds(route, church, join)) continue;
    const oneWay = estimateAccessMinutes(join.offKm);
    churches.push({ church, join, toMin: oneWay, backMin: oneWay, exact: false });
  }
  churches.sort((a, b) => a.join.alongKm - b.join.alongKm);
  return { churches, queries, narrowed };
}
